import { queryOptions } from '@tanstack/react-query';
import { api, apiMutation, mutation } from '../../../api/http';
export { createTag, tagListQuery } from '../../tags/api/tags';
export type { TagResponse } from '../../tags/api/tags';

export type QuestionStatus = 'Draft' | 'Active' | 'Retired';
export type QuestionLabel = 'A' | 'B' | 'C' | 'D';

export interface QuestionSummaryResponse {
  id: string;
  stem: string;
  status: QuestionStatus;
  revisionNumber: number;
  version: number;
  updatedAt: string;
}

export interface QuestionListResponse {
  items: QuestionSummaryResponse[];
  page: number;
  pageSize: number;
  total: number;
}

export interface QuestionOptionResponse {
  id: string;
  label: QuestionLabel;
  text: string;
}

export interface QuestionTagResponse {
  id: string;
  name: string;
  slug: string;
}

export interface QuestionEditorResponse {
  id: string;
  status: QuestionStatus;
  version: number;
  revisionNumber: number;
  stem: string;
  codeSnippet: string | null;
  codeLanguage: string | null;
  explanation: string | null;
  difficulty: number | null;
  correctOptionId: string;
  source: string | null;
  options: QuestionOptionResponse[];
  tags: QuestionTagResponse[];
  articles: QuestionArticleResponse[];
  updatedAt: string;
}

export interface QuestionArticleResponse {
  id: string;
  slug: string;
  title: string;
  sortOrder: number;
}

export interface QuestionEditorValues {
  stem: string;
  codeSnippet: string;
  codeLanguage: string;
  explanation: string;
  difficulty: string;
  correctLabel: QuestionLabel;
  source: string;
  options: { label: QuestionLabel; text: string }[];
  tagIds: string[];
  articleIds: string[];
}

export interface CreateQuestionSubmission {
  stem: string;
  codeSnippet: string | null;
  codeLanguage: string | null;
  explanation: string | null;
  difficulty: number | null;
  correctLabel: QuestionLabel;
  source: string | null;
  options: { label: QuestionLabel; text: string }[];
  tagIds: string[];
  articleIds: string[];
}

export interface SaveQuestionSubmission extends CreateQuestionSubmission {
  expectedVersion: number;
}

export interface QuestionImportPreview {
  batchId: string;
  status: string;
  validCount: number;
  warningCount: number;
  errorCount: number;
  expiresAt: string;
  items: {
    row: number;
    externalId: string | null;
    state: string;
    errors: { code: string; message: string }[];
    warnings: { code: string; message: string }[];
  }[];
}

export interface QuestionImportCommitSummary {
  batchId: string;
  mode: 'Insert' | 'Upsert';
  inserted: number;
  updated: number;
  unchanged: number;
}

export const questionQueryKeys = {
  all: ['internal', 'questions'] as const,
  import: (batchId: string) => ['internal', 'question-imports', batchId] as const,
  list: (filter: QuestionListFilter) => ['internal', 'questions', filter] as const,
  draft: (id: string) => ['internal', 'questions', id] as const,
};

export const questionImportQuery = (batchId: string) =>
  queryOptions({
    queryKey: questionQueryKeys.import(batchId),
    queryFn: ({ signal }) =>
      api<QuestionImportPreview>(`/internal/questions/import/${batchId}`, { signal }),
  });

export async function previewQuestionImport(file: File) {
  const csrf = await api<{ requestToken: string }>('/auth/csrf');
  const body = new FormData();
  body.append('File', file);
  return api<QuestionImportPreview>('/internal/questions/import', {
    method: 'POST',
    headers: { 'X-CSRF-TOKEN': csrf.requestToken },
    body,
  });
}

export function commitQuestionImport(batchId: string, mode: 'Insert' | 'Upsert') {
  return mutation<QuestionImportCommitSummary>(`/internal/questions/import/${batchId}/commit`, {
    mode,
  });
}

export async function discardQuestionImport(batchId: string) {
  const csrf = await api<{ requestToken: string }>('/auth/csrf');
  return api<void>(`/internal/questions/import/${batchId}`, {
    method: 'DELETE',
    headers: { 'X-CSRF-TOKEN': csrf.requestToken },
  });
}

export interface QuestionListFilter {
  status: string;
  query: string;
  page: number;
}

export const questionListQuery = (filter: QuestionListFilter) =>
  queryOptions({
    queryKey: questionQueryKeys.list(filter),
    queryFn: ({ signal }) => {
      const params = new URLSearchParams({ page: String(filter.page), pageSize: '20' });
      if (filter.status) params.set('status', filter.status);
      if (filter.query) params.set('q', filter.query);
      return api<QuestionListResponse>(`/internal/questions?${params}`, { signal });
    },
  });

export const questionDraftQuery = (id: string) =>
  queryOptions({
    queryKey: questionQueryKeys.draft(id),
    queryFn: ({ signal }) => api<QuestionEditorResponse>(`/internal/questions/${id}`, { signal }),
  });

export function createQuestion(submission: CreateQuestionSubmission) {
  return mutation<QuestionEditorResponse>('/internal/questions', submission);
}

export function saveQuestion(id: string, submission: SaveQuestionSubmission) {
  return apiMutation<QuestionEditorResponse>(`/internal/questions/${id}`, 'PUT', submission);
}

export function changeQuestionStatus(
  id: string,
  action: 'activate' | 'retire',
  expectedVersion: number,
) {
  return mutation<QuestionEditorResponse>(`/internal/questions/${id}/${action}`, {
    expectedVersion,
  });
}
